import { BufferAttribute, BufferGeometry, LineBasicMaterial, LineSegments } from 'three';

import type { DebugManager } from '../debug-manager.class';
import type { DebugPathConfig } from '../types';

export interface AddPathParams {
	config: DebugPathConfig;
}

/** Build vertex pairs for LineSegments: [P0,P1, P1,P2, P2,P3, ...] */
const buildSegmentPositions = (points: DebugPathConfig['points']): Float32Array => {
	const segmentCount = points.length - 1;
	const positions = new Float32Array(segmentCount * 2 * 3);

	for (let i = 0; i < segmentCount; i++) {
		const a = points[i];
		const b = points[i + 1];
		const offset = i * 6;
		positions[offset] = a.x;
		positions[offset + 1] = a.y;
		positions[offset + 2] = a.z;
		positions[offset + 3] = b.x;
		positions[offset + 4] = b.y;
		positions[offset + 5] = b.z;
	}

	return positions;
};

const hasInvalidPoint = (config: DebugPathConfig): boolean => {
	for (let i = 0; i < config.points.length; i++) {
		const p = config.points[i];
		if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) {
			console.warn(`DebugManager.addPath: Skipping path '${config.id}' with invalid point at index ${i}`);
			return true;
		}
	}
	return false;
};

const buildPathGeometry = (config: DebugPathConfig): LineSegments | null => {
	if (hasInvalidPoint(config)) return null;

	const positions = buildSegmentPositions(config.points);
	const geometry = new BufferGeometry();
	geometry.setAttribute('position', new BufferAttribute(positions, 3));

	const material = new LineBasicMaterial({ color: config.color ?? 0xffff00, depthTest: false });
	const line = new LineSegments(geometry, material);
	line.name = `debug_path_${config.id}`;
	line.renderOrder = 999;
	line.userData.isInterface = true;
	line.userData.type = 'debugVisualization';

	return line;
};

/**
 * Add a debug path (multi-point polyline) rendered as connected line segments.
 * Stored in the lines map for consistent visibility/cleanup handling.
 */
export const addPathCfn = (
	self: DebugManager,
	{ config }: AddPathParams
): LineSegments | null => {
	const { id, points } = config;

	if (points.length < 2) {
		console.warn(`DebugManager.addPath: Skipping path '${id}' with fewer than 2 points`);
		return null;
	}

	// Remove existing line with same ID
	if (self.hasLine(id)) {
		self.removeLine(id);
	}

	const line = buildPathGeometry(config);
	if (!line) return null;

	line.visible = self.visible;
	self.scene.add(line);
	self.registerLine(id, line);

	return line;
};
